import i18n from '../../i18n'
import { ROLE_SLUGS, EXPERIENCE_ROLES } from '../constants/sharing.constants'

/**
 * Slugs a share recipient can have. `owner` is excluded on purpose: owned
 * experiences never show a recipient label or box (story 02.2, AC-6).
 */
const RECIPIENT_ROLE_SLUGS = new Set(
  Object.entries(ROLE_SLUGS)
    .filter(([id]) => Number(id) !== EXPERIENCE_ROLES.OWNER)
    .map(([, slug]) => slug)
)

/**
 * The ONE place a role turns into recipient-facing text. The chip on the card
 * (02.1) and the box on the shared experience (02.2) are the same text in two
 * places: a second map would let them drift apart, and nothing would notice.
 *
 * Input is the role SLUG as served by `get-user-experiences` — 'listener', not 3.
 *
 * Every accessor answers null for the same three cases: no role, an unknown
 * slug, and `owner`. Callers render nothing rather than a placeholder — a NULL
 * role is reachable (`experience_role` is nullable with ON DELETE SET NULL) and
 * the honest response to it is silence.
 */
const isRecipientRole = (role: string | null | undefined): role is string =>
  !!role && RECIPIENT_ROLE_SLUGS.has(role)

/** i18n key of the role title, or null. */
export const roleTitleKey = (role: string | null | undefined): string | null =>
  isRecipientRole(role) ? `sharedExperience.roleTitle.${role}` : null

/** i18n key of the second-person description, or null. */
export const roleHintKey = (role: string | null | undefined): string | null =>
  isRecipientRole(role) ? `sharedExperience.roleHints.${role}` : null

/**
 * Already-translated role title, for callers that need a plain string rather
 * than a key — the card chip, which passes it as a prop.
 */
export const roleLabel = (role: string | null | undefined): string | null => {
  const key = roleTitleKey(role)
  return key ? i18n.t(key) : null
}
